import { useMemo } from 'react'

import { type OrderDraftItemFieldErrors } from '@webapp/modules/purchasing/business-logic/types/OrderDraftItemFieldErrors'
import { type RejectedOrderDraftItemValues } from '@webapp/modules/purchasing/business-logic/types/RejectedOrderDraftItemValues'
import { OrderDraftItemMutationStatuses } from './OrderDraftItemMutationStatusEnum'
import { useOrderDraftItemMutationStatusContext } from './useOrderDraftItemMutationStatusContext'

export type OrderDraftItemMutationKind = 'create' | 'edit' | 'delete'

export interface IOrderDraftItemMutationStatusCallbacks {
  onMutationStart: (itemId: number) => number
  onMutationComplete: (itemId: number, mutationSeq: number | undefined) => void
  onMutationError: (
    itemId: number,
    mutationSeq: number | undefined,
    fieldErrors?: OrderDraftItemFieldErrors,
    rejectedValues?: RejectedOrderDraftItemValues
  ) => void
  // For items that leave the cache outright (client-only deletes).
  onMutationCleanup: (itemId: number) => void
}

const NO_REJECTED_VALUES: RejectedOrderDraftItemValues = {
  currencyId: undefined,
  vendorId: undefined,
}

const IN_PROGRESS_STATUS_BY_KIND = {
  create: OrderDraftItemMutationStatuses.CREATING,
  edit: OrderDraftItemMutationStatuses.UPDATING,
  delete: OrderDraftItemMutationStatuses.DELETING,
} as const

const FAILED_STATUS_BY_KIND = {
  create: OrderDraftItemMutationStatuses.FAILED_TO_CREATE,
  edit: OrderDraftItemMutationStatuses.FAILED_TO_UPDATE,
  delete: OrderDraftItemMutationStatuses.FAILED_TO_DELETE,
} as const

// Wires the item mutation-status context to the generic lifecycle callbacks,
// mapping each mutation kind to its status enum. Consumed at the feature-set
// level so feature slices never touch the context directly.
export const useOrderDraftItemMutationStatusCallbacks = (
  kind: OrderDraftItemMutationKind
): IOrderDraftItemMutationStatusCallbacks => {
  const {
    setItemMutationStatus,
    clearItemMutationStatus,
    setItemFieldErrors,
    clearItemFieldErrors,
  } = useOrderDraftItemMutationStatusContext()

  return useMemo(
    () => ({
      onMutationStart: (itemId) =>
        setItemMutationStatus(itemId, IN_PROGRESS_STATUS_BY_KIND[kind]),
      onMutationComplete: (itemId, mutationSeq) => {
        if (mutationSeq === undefined) return
        setItemMutationStatus(
          itemId,
          OrderDraftItemMutationStatuses.COMPLETED,
          mutationSeq
        )
        // A successful edit resolves the error; a delete removes the item it belonged to.
        if (kind === 'edit' || kind === 'delete') clearItemFieldErrors(itemId)
      },
      onMutationError: (itemId, mutationSeq, fieldErrors, rejectedValues) => {
        if (mutationSeq === undefined) return
        setItemMutationStatus(itemId, FAILED_STATUS_BY_KIND[kind], mutationSeq)
        if (fieldErrors) {
          setItemFieldErrors(
            itemId,
            fieldErrors,
            rejectedValues ?? NO_REJECTED_VALUES
          )
        }
      },
      // Client-only delete: nothing else would clear this id's field error.
      onMutationCleanup: (itemId) => {
        clearItemMutationStatus(itemId)
        clearItemFieldErrors(itemId)
      },
    }),
    [
      kind,
      setItemMutationStatus,
      clearItemMutationStatus,
      setItemFieldErrors,
      clearItemFieldErrors,
    ]
  )
}
